import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, IndianRupee, Info, Plus, Save, Send, Trash2 } from "lucide-react";
import { api, errMsg, inr } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { EmptyState, Money, PageHeader, StatusBadge, TableSkeleton, fmtDate } from "../components/erp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Separator } from "../components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { PaymentDialog } from "./Payments";
import AddressFields, { EMPTY_ADDRESS, addressFromParty, addressText } from "../components/AddressFields";

const SUPPLIER_TYPES = ["supplier", "vendor", "contractor", "dealer"];

export const DOC_CFG = {
  quotation: {
    title: "Quotation", plural: "Quotations", api: "/quotations", route: "/quotations",
    partyLabel: "Customer", dateLabel: "Quotation Date", validLabel: "Valid Until",
    issueLabel: "Send Quotation", refLabel: "Reference / Enquiry No",
  },
  sales_order: {
    title: "Sales Order", plural: "Sales Orders", api: "/sales-orders", route: "/sales-orders",
    partyLabel: "Customer", dateLabel: "Order Date", validLabel: "Delivery Date",
    issueLabel: "Confirm Order", refLabel: "Customer PO Number",
  },
  proforma: {
    title: "Proforma Invoice", plural: "Proforma Invoices", api: "/proformas", route: "/proformas",
    partyLabel: "Customer", dateLabel: "Proforma Date", validLabel: "Valid Until",
    issueLabel: "Issue Proforma", refLabel: "Quotation / Order Ref",
  },
  purchase_order: {
    title: "Purchase Order", plural: "Purchase Orders", api: "/purchase-orders", route: "/purchases",
    newRoute: "/purchases/orders/new", partyLabel: "Supplier", dateLabel: "PO Date",
    validLabel: "Expected Delivery", issueLabel: "Submit for Approval", refLabel: "Supplier Quote Ref",
    supplier: true,
  },
  purchase_invoice: {
    title: "Purchase Invoice", plural: "Purchase Invoices", api: "/purchase-invoices", route: "/purchases",
    newRoute: "/purchases/invoices/new", partyLabel: "Supplier", dateLabel: "Bill Date",
    validLabel: "Due Date", issueLabel: "Post Purchase", refLabel: "Supplier Invoice No",
    supplier: true,
  },
};

const Tip = ({ text }) => (
  <TooltipProvider><Tooltip>
    <TooltipTrigger asChild><Info className="ml-1 inline h-3.5 w-3.5 text-slate-400 cursor-help" /></TooltipTrigger>
    <TooltipContent className="max-w-xs text-xs bg-slate-900 text-white">{text}</TooltipContent>
  </Tooltip></TooltipProvider>
);

const newLine = () => ({ item_id: null, name: "", hsn: "", unit: "Nos", qty: 1, rate: 0, discount_pct: 0, tax_rate: 18 });

export function useDocTotals(lines, charges, intra) {
  return useMemo(() => {
    let subtotal = 0, disc = 0, taxable = 0, cgst = 0, sgst = 0, igst = 0;
    for (const l of lines) {
      const base = (+l.qty || 0) * (+l.rate || 0);
      const d = base * (+l.discount_pct || 0) / 100;
      const t = base - d;
      const tax = t * (+l.tax_rate || 0) / 100;
      subtotal += base; disc += d; taxable += t;
      if (intra) { cgst += tax / 2; sgst += tax / 2; } else igst += tax;
    }
    const grand = taxable + cgst + sgst + igst + (+charges || 0);
    const total = Math.round(grand * 100) / 100;
    const rounded = Math.round(grand);
    return { subtotal, disc, taxable, cgst, sgst, igst, grand: total, roundOff: rounded - total, total: rounded };
  }, [lines, charges, intra]);
}

export function DocList({ kind }) {
  const cfg = DOC_CFG[kind];
  const { activeId } = useCompany();
  const [data, setData] = useState(null);
  const nav = useNavigate();
  const newRoute = cfg.newRoute || `${cfg.route}/new`;

  useEffect(() => {
    if (!activeId) return;
    setData(null);
    api.get(`${cfg.api}?limit=200`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [activeId, kind]); // eslint-disable-line

  return (
    <div data-testid={`${kind}-list-page`}>
      <PageHeader title={cfg.plural} testId={`${kind}-list-title`}
        sub={kind === "proforma" ? "Pre-invoices with advance tracking — convert to tax invoice in one click" : ""}>
        <Button data-testid={`new-${kind}-btn`} onClick={() => nav(newRoute)}
          className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Plus className="mr-1.5 h-4 w-4" /> New {cfg.title}</Button>
      </PageHeader>
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        {!data ? <TableSkeleton /> : data.items.length === 0 ? (
          <EmptyState testId={`${kind}-empty`} title={`No ${cfg.plural.toLowerCase()} yet`}
            body={kind === "quotation" ? "Start the sales chain: Quotation → Sales Order → Proforma → Invoice." : ""}
            actionLabel={`New ${cfg.title}`} actionTo={newRoute} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={`${kind}-table`}>
              <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="p-3">Number</th><th className="p-3">Date</th><th className="p-3">{cfg.partyLabel}</th>
                <th className="p-3">{cfg.validLabel}</th><th className="p-3 text-right">Total</th><th className="p-3">Status</th></tr></thead>
              <tbody>
                {data.items.map((d) => (
                  <tr key={d.id} data-testid={`${kind}-row-${d.doc_no}`} onClick={() => nav(`${cfg.route}/${d.id}`)}
                    className="cursor-pointer border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                    <td className="p-3 font-mono text-xs font-semibold">{d.doc_no}</td>
                    <td className="p-3">{fmtDate(d.date)}</td>
                    <td className="p-3 font-medium max-w-[220px] truncate">{d.party_name}</td>
                    <td className="p-3">{fmtDate(d.valid_until)}</td>
                    <td className="p-3 text-right"><Money value={d.total} className="font-semibold" /></td>
                    <td className="p-3"><StatusBadge status={d.status.charAt(0).toUpperCase() + d.status.slice(1).replace(/_/g, " ")} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="mt-2 text-xs text-slate-400">{data?.total ?? 0} {cfg.plural.toLowerCase()}</p>
    </div>
  );
}

export function DocForm({ kind }) {
  const cfg = DOC_CFG[kind];
  const { activeId, active } = useCompany();
  const nav = useNavigate();
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [partyId, setPartyId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [validUntil, setValidUntil] = useState("");
  const [placeOfSupply, setPlaceOfSupply] = useState("");
  const [billingAddress, setBillingAddress] = useState(EMPTY_ADDRESS);
  const [shippingAddress, setShippingAddress] = useState(EMPTY_ADDRESS);
  const [reference, setReference] = useState("");
  const [lines, setLines] = useState([newLine()]);
  const [charges, setCharges] = useState(0);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!activeId) return;
    api.get("/parties?limit=200").then((r) => {
      const all = r.data.items.filter((p) => !p.archived);
      const filtered = cfg.supplier
        ? all.filter((p) => SUPPLIER_TYPES.includes(p.type))
        : all.filter((p) => !["supplier", "vendor"].includes(p.type));
      setParties(filtered.length ? filtered : all);
    }).catch(() => {});
    api.get("/items?limit=200").then((r) => setItems(r.data.items)).catch(() => {});
  }, [activeId, kind]); // eslint-disable-line

  useEffect(() => {
    const p = parties.find((x) => x.id === partyId);
    if (p) {
      setPlaceOfSupply((v) => v || p.state || "");
      const b = addressFromParty(p, "billing");
      const sh = addressFromParty(p, "shipping");
      setBillingAddress(b);
      setShippingAddress(sh.line1 ? sh : b);
    }
  }, [partyId, parties]);

  const intra = useMemo(() => {
    const supply = (placeOfSupply || "").trim().toLowerCase();
    const comp = (active?.state || "").trim().toLowerCase();
    return supply && comp ? supply === comp : true;
  }, [placeOfSupply, active]);

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const pickItem = (i, itemId) => {
    if (itemId === "custom") { setLine(i, "item_id", null); return; }
    const it = items.find((x) => x.id === itemId);
    if (it) setLines((ls) => ls.map((l, j) => j === i ? { ...l, item_id: it.id, name: it.name, hsn: it.hsn, unit: it.unit, rate: cfg.supplier ? it.purchase_price : it.sale_price, tax_rate: it.tax_rate } : l));
  };
  const calc = useDocTotals(lines, charges, intra);

  const save = async (saveAs) => {
    if (!partyId) { toast.error(`Select a ${cfg.partyLabel.toLowerCase()}`); return; }
    const validLines = lines.filter((l) => l.name.trim() && +l.qty > 0);
    if (validLines.length === 0) { toast.error("Add at least one item line"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(cfg.api, {
        party_id: partyId, date, valid_until: validUntil || null, place_of_supply: placeOfSupply,
        billing_address_details: billingAddress, billing_address: addressText(billingAddress),
        shipping_address_details: shippingAddress, shipping_address: addressText(shippingAddress),
        reference, items: validLines.map((l) => ({ ...l, qty: +l.qty, rate: +l.rate, discount_pct: +l.discount_pct, tax_rate: +l.tax_rate })),
        additional_charges: +charges || 0, notes, save_as: saveAs,
      });
      toast.success(`${cfg.title} ${data.doc_no} ${saveAs === "issue" || saveAs === "posted" ? "issued" : "saved as draft"}`);
      nav(cfg.supplier ? cfg.route : `${cfg.route}/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid={`${kind}-form-page`} className="max-w-5xl">
      <Button variant="ghost" size="sm" className="mb-3 -ml-2" onClick={() => nav(cfg.route)} data-testid={`back-to-${kind}`}>
        <ArrowLeft className="mr-1 h-4 w-4" /> {cfg.plural}
      </Button>
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-5 sm:p-6 space-y-6">
        <div className="border-b border-slate-100 dark:border-slate-800 pb-4">
          <h1 className="font-display text-xl font-bold" data-testid={`${kind}-form-title`}>New {cfg.title}</h1>
          <p className="text-xs text-slate-500 mt-0.5">{active?.name} · GSTIN {active?.gstin || "—"}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="sm:col-span-2">
            <Label>{cfg.partyLabel} *</Label>
            <Select value={partyId} onValueChange={setPartyId}>
              <SelectTrigger data-testid={`${kind}-party`} className="mt-1.5"><SelectValue placeholder={`Select ${cfg.partyLabel.toLowerCase()}`} /></SelectTrigger>
              <SelectContent>{parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.code})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>{cfg.dateLabel}</Label><Input data-testid={`${kind}-date`} type="date" className="mt-1.5" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div><Label>{cfg.validLabel}</Label><Input data-testid={`${kind}-valid-until`} type="date" className="mt-1.5" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} /></div>
          <div>
            <Label>Place of Supply <Tip text="Same state as your company → CGST + SGST, different state → IGST." /></Label>
            <Input data-testid={`${kind}-place-of-supply`} className="mt-1.5" value={placeOfSupply} onChange={(e) => setPlaceOfSupply(e.target.value)} placeholder={active?.state || "State"} />
            <p className="mt-1 text-xs">
              <span className={`rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold ${intra ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400" : "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400"}`}
                data-testid="tax-mode-badge">{intra ? "INTRA-STATE · CGST + SGST" : "INTER-STATE · IGST"}</span>
            </p>
          </div>
          <div><Label>{cfg.refLabel}</Label><Input data-testid={`${kind}-reference`} className="mt-1.5" value={reference} onChange={(e) => setReference(e.target.value)} /></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <AddressFields title="Billing Address" value={billingAddress} onChange={setBillingAddress} icon="billing" copyLabel="Use Customer / Supplier Address" onCopy={() => { const p = parties.find((x) => x.id === partyId); if (p) setBillingAddress(addressFromParty(p, "billing")); }} />
          <AddressFields title="Shipping Address" value={shippingAddress} onChange={setShippingAddress} icon="shipping" copyLabel="Use Billing Address" onCopy={() => setShippingAddress({ ...billingAddress })} />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-semibold">Line Items</Label>
            <Button size="sm" variant="outline" data-testid="add-line-btn" onClick={() => setLines((ls) => [...ls, newLine()])}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Add Line
            </Button>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
            <table className="w-full text-sm min-w-[820px]" data-testid="doc-lines">
              <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="p-2.5 w-64">Item</th><th className="p-2.5">HSN</th><th className="p-2.5 w-20">Qty</th>
                <th className="p-2.5 w-28">Rate ₹</th><th className="p-2.5 w-20">Disc %</th><th className="p-2.5 w-24">GST %</th>
                <th className="p-2.5 text-right w-28">Amount</th><th className="p-2.5 w-10"></th></tr></thead>
              <tbody>
                {lines.map((l, i) => {
                  const base = (+l.qty || 0) * (+l.rate || 0);
                  const t = base - base * (+l.discount_pct || 0) / 100;
                  const amt = t + t * (+l.tax_rate || 0) / 100;
                  return (
                    <tr key={i} className="border-b border-slate-100 dark:border-slate-800 align-top">
                      <td className="p-2">
                        <Select value={l.item_id || (l.name ? "custom" : "")} onValueChange={(v) => pickItem(i, v)}>
                          <SelectTrigger data-testid={`line-item-${i}`} className="h-9"><SelectValue placeholder="Select item" /></SelectTrigger>
                          <SelectContent>
                            {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}
                            <SelectItem value="custom">— Custom line —</SelectItem>
                          </SelectContent>
                        </Select>
                        {!l.item_id && (
                          <Input data-testid={`line-name-${i}`} className="mt-1.5 h-8 text-xs" placeholder="Item / description"
                            value={l.name} onChange={(e) => setLine(i, "name", e.target.value)} />
                        )}
                      </td>
                      <td className="p-2"><Input className="h-9 w-20 font-mono text-xs" value={l.hsn} onChange={(e) => setLine(i, "hsn", e.target.value)} /></td>
                      <td className="p-2"><Input data-testid={`line-qty-${i}`} type="number" min="0" className="h-9" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} /></td>
                      <td className="p-2"><Input data-testid={`line-rate-${i}`} type="number" min="0" className="h-9" value={l.rate} onChange={(e) => setLine(i, "rate", e.target.value)} /></td>
                      <td className="p-2"><Input type="number" min="0" max="100" className="h-9" value={l.discount_pct} onChange={(e) => setLine(i, "discount_pct", e.target.value)} /></td>
                      <td className="p-2">
                        <Select value={String(l.tax_rate)} onValueChange={(v) => setLine(i, "tax_rate", +v)}>
                          <SelectTrigger data-testid={`line-tax-${i}`} className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{[0, 5, 12, 18, 28].map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="p-2 text-right font-mono text-sm pt-3">{inr(amt)}</td>
                      <td className="p-2">
                        <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-400 hover:text-red-500"
                          data-testid={`line-remove-${i}`} onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} disabled={lines.length === 1}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div><Label>Additional Charges (₹)</Label><Input data-testid={`${kind}-charges`} type="number" min="0" className="mt-1.5" value={charges} onChange={(e) => setCharges(e.target.value)} /></div>
            <div><Label>Notes / Terms</Label><Textarea className="mt-1.5" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          </div>
          <div className="rounded-xl bg-slate-50 dark:bg-slate-800/50 p-5 space-y-2 text-sm h-fit" data-testid="doc-totals">
            {[["Subtotal", calc.subtotal], ["Discount", -calc.disc], ["Taxable Amount", calc.taxable],
              ...(intra ? [["CGST", calc.cgst], ["SGST", calc.sgst]] : [["IGST", calc.igst]]),
              ["Additional Charges", +charges || 0], ["Round Off", calc.roundOff],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between text-slate-600 dark:text-slate-300">
                <span>{k}</span><span className="font-mono">{inr(v)}</span>
              </div>
            ))}
            <div className="border-t border-slate-200 dark:border-slate-700 pt-2 flex justify-between items-center">
              <span className="font-display font-bold">Grand Total</span>
              <span className="font-mono text-xl font-bold" data-testid="doc-grand-total">{inr(calc.total)}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 dark:border-slate-800 pt-4">
          <Button variant="outline" data-testid="save-draft-btn" disabled={busy} onClick={() => save("draft")}>
            <Save className="mr-1.5 h-4 w-4" /> Save Draft
          </Button>
          <Button data-testid="issue-doc-btn" disabled={busy} onClick={() => save(kind === "purchase_invoice" ? "posted" : "issue")}
            className="bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900">
            <Send className="mr-1.5 h-4 w-4" /> {busy ? "Saving..." : cfg.issueLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function DocDetail({ kind }) {
  const cfg = DOC_CFG[kind];
  const { activeId } = useCompany();
  const [doc, setDoc] = useState(null);
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const nav = useNavigate();
  const { id } = useParams();

  const load = () => api.get(`${cfg.api}/${id}`).then((r) => setDoc(r.data)).catch((e) => { toast.error(errMsg(e)); nav(cfg.route); });
  useEffect(() => { if (activeId) { setDoc(null); load(); } }, [id, activeId]); // eslint-disable-line

  if (!doc) return <TableSkeleton />;
  const c = doc.company || {};
  const status = doc.status;

  const setStatus = async (s) => {
    try {
      await api.post(`${cfg.api}/${id}/status`, { status: s });
      toast.success(`Status updated to ${s.replace(/_/g, " ")}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const convert = async (to) => {
    try {
      let r;
      if (kind === "quotation") {
        r = await api.post(`/quotations/${id}/convert`);
        toast.success(`Sales order ${r.data.doc_no} created`);
        nav(`/sales-orders/${r.data.id}`);
      } else if (kind === "sales_order") {
        r = await api.post(`/sales-orders/${id}/convert?to=${to}`);
        if (r.data.invoice_id) { toast.success(`Invoice ${r.data.invoice_no} posted`); nav(`/invoices/${r.data.invoice_id}`); }
        else { toast.success(`Proforma ${r.data.doc_no} created`); nav(`/proformas/${r.data.id}`); }
      } else if (kind === "proforma") {
        r = await api.post(`/proformas/${id}/convert`);
        toast.success(`Invoice ${r.data.invoice_no} posted` + (r.data.advance_applied > 0 ? ` · advance ${inr(r.data.advance_applied)} applied` : ""));
        nav(`/invoices/${r.data.invoice_id}`);
      }
    } catch (e) { toast.error(errMsg(e)); }
  };

  const ACTIONS = {
    quotation: [
      status === "draft" && { label: "Mark as Sent", act: () => setStatus("sent"), testId: "quote-send" },
      ["draft", "sent"].includes(status) && { label: "Accept", act: () => setStatus("accepted"), testId: "quote-accept" },
      ["draft", "sent"].includes(status) && { label: "Reject", act: () => setStatus("rejected"), testId: "quote-reject", outline: true },
      status === "accepted" && { label: "Convert to Sales Order", act: () => convert(), testId: "quote-convert", primary: true },
    ],
    sales_order: [
      status === "draft" && { label: "Confirm Order", act: () => setStatus("confirmed"), testId: "so-confirm", primary: true },
      ["confirmed", "in_progress"].includes(status) && { label: "Convert to Proforma", act: () => convert("proforma"), testId: "so-to-proforma", outline: true },
      ["draft", "confirmed", "in_progress"].includes(status) && { label: "Convert to Invoice", act: () => convert("invoice"), testId: "so-to-invoice", primary: true },
      !["converted", "cancelled"].includes(status) && { label: "Cancel Order", act: () => setStatus("cancelled"), testId: "so-cancel", outline: true },
    ],
    proforma: [
      status === "draft" && { label: "Issue Proforma", act: () => setStatus("issued"), testId: "proforma-issue", primary: true },
      ["issued", "sent"].includes(status) && { label: "Record Advance", act: () => setAdvanceOpen(true), testId: "proforma-advance", outline: true },
      ["draft", "issued", "sent", "accepted"].includes(status) && { label: "Convert to Sales Invoice", act: () => convert(), testId: "proforma-convert", primary: true, confirm: true },
      !["converted", "cancelled"].includes(status) && { label: "Cancel", act: () => setStatus("cancelled"), testId: "proforma-cancel", outline: true },
    ],
  };

  return (
    <div data-testid={`${kind}-detail-page`} className="max-w-4xl">
      <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" className="-ml-2" onClick={() => nav(cfg.route)} data-testid={`back-to-${kind}-list`}>
          <ArrowLeft className="mr-1 h-4 w-4" /> {cfg.plural}
        </Button>
        <div className="flex gap-2 flex-wrap">
          {(ACTIONS[kind] || []).filter(Boolean).map((a) =>
            a.confirm ? (
              <AlertDialog key={a.testId}>
                <AlertDialogTrigger asChild>
                  <Button data-testid={a.testId} className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">{a.label} <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
                </AlertDialogTrigger>
                <AlertDialogContent aria-describedby={undefined} className="bg-white dark:bg-slate-900">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Convert {doc.doc_no} to a Sales Invoice?</AlertDialogTitle>
                    <AlertDialogDescription>
                      A posted tax invoice will be created for {doc.party_name} worth {inr(doc.total)}.
                      {kind === "proforma" && " Any advance recorded against this proforma will be applied automatically."}
                      {" "}Stock and ledger update immediately.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="convert-no">Back</AlertDialogCancel>
                    <AlertDialogAction data-testid="convert-yes" onClick={() => convert()} className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">Convert</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <Button key={a.testId} data-testid={a.testId} variant={a.primary ? "default" : "outline"} onClick={a.act}
                className={a.primary ? "bg-slate-900 dark:bg-slate-100 dark:text-slate-900" : ""}>{a.label}</Button>
            )
          )}
          <Button variant="outline" data-testid="print-doc-btn" onClick={() => window.print()}>Print / PDF</Button>
        </div>
      </div>

      <div className="print-area rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-8" data-testid="doc-preview">
        <div className="flex flex-wrap justify-between gap-4">
          <div>
            <h1 className="font-display text-lg font-bold leading-tight">{c.name}</h1>
            <p className="text-xs text-slate-500">{[c.address, c.city, c.state].filter(Boolean).join(", ")}</p>
            {c.gstin && <p className="text-xs font-mono text-slate-500 mt-0.5">GSTIN: {c.gstin}</p>}
          </div>
          <div className="text-right">
            <p className="font-display text-2xl font-extrabold tracking-tight uppercase">{cfg.title}</p>
            <p className="font-mono text-sm font-semibold mt-1" data-testid="doc-no">{doc.doc_no}</p>
            <p className="text-xs text-slate-500 mt-1">Date: {fmtDate(doc.date)}{doc.valid_until ? ` · ${cfg.validLabel}: ${fmtDate(doc.valid_until)}` : ""}</p>
            <div className="mt-2"><StatusBadge status={status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ")} /></div>
          </div>
        </div>

        {doc.source && (doc.source.quotation_no || doc.source.sales_order_no) && (
          <p className="mt-3 text-xs text-slate-500">
            {doc.source.quotation_no && <>From quotation <span className="font-mono font-semibold">{doc.source.quotation_no}</span></>}
            {doc.source.sales_order_no && <>From sales order <span className="font-mono font-semibold">{doc.source.sales_order_no}</span></>}
          </p>
        )}
        {doc.reference && <p className="mt-1 text-xs text-slate-500">Reference: <span className="font-mono">{doc.reference}</span></p>}

        <Separator className="my-5" />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">{cfg.partyLabel}</p>
            <p className="font-semibold">{doc.party_name}</p>
            <p className="text-xs text-slate-500">{doc.billing_address || [doc.party?.billing_address, doc.party?.city, doc.party?.state].filter(Boolean).join(", ")}</p>
            {doc.party_gstin && <p className="text-xs font-mono text-slate-500 mt-0.5">GSTIN: {doc.party_gstin}</p>}
          </div>
          <div className="text-sm">
            <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Ship To</p>
            <p className="text-xs text-slate-500">{addressText(doc.shipping_address_details) || doc.shipping_address || [doc.party?.shipping_address, doc.party?.city, doc.party?.state].filter(Boolean).join(", ")}</p>
          </div>
          <div className="sm:text-right text-sm">
            <p className="text-xs text-slate-500">Place of Supply: <b>{doc.place_of_supply || "—"}</b></p>
            <p className="text-xs text-slate-500">{doc.intra_state ? "Intra-state (CGST + SGST)" : "Inter-state (IGST)"}</p>
          </div>
        </div>

        <div className="mt-5 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-sm min-w-[600px]" data-testid="doc-items-table">
            <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <th className="p-2.5">#</th><th className="p-2.5">Item</th><th className="p-2.5">HSN</th>
              <th className="p-2.5 text-right">Qty</th><th className="p-2.5 text-right">Rate</th>
              <th className="p-2.5 text-right">Taxable</th><th className="p-2.5 text-right">Tax</th>
              <th className="p-2.5 text-right">Total</th></tr></thead>
            <tbody>
              {doc.items.map((l, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="p-2.5 text-slate-400">{i + 1}</td>
                  <td className="p-2.5 font-medium">{l.name}{l.discount_pct ? <span className="ml-1 text-xs text-emerald-600">(-{l.discount_pct}%)</span> : null}</td>
                  <td className="p-2.5 font-mono text-xs text-slate-500">{l.hsn || "—"}</td>
                  <td className="p-2.5 text-right font-mono">{l.qty} {l.unit}</td>
                  <td className="p-2.5 text-right font-mono">{inr(l.rate)}</td>
                  <td className="p-2.5 text-right font-mono">{inr(l.taxable)}</td>
                  <td className="p-2.5 text-right font-mono text-xs">{inr(l.cgst + l.sgst + l.igst)}</td>
                  <td className="p-2.5 text-right font-mono font-semibold">{inr(l.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="text-xs text-slate-500">
            {doc.notes && <><p className="font-semibold text-slate-700 dark:text-slate-300">Notes / Terms</p><p className="mt-1">{doc.notes}</p></>}
            {kind === "proforma" && doc.advance_payments?.length > 0 && (
              <div className="mt-4 no-print">
                <p className="font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Advances received</p>
                {doc.advance_payments.map((p) => (
                  <div key={p.id} className="flex justify-between rounded-lg bg-slate-50 dark:bg-slate-800/50 px-3 py-2 text-sm mb-1.5" data-testid={`advance-${p.payment_no}`}>
                    <span className="font-mono text-xs">{p.payment_no} · {fmtDate(p.date)}</span>
                    <Money value={p.amount} className="font-semibold text-emerald-600" />
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-1.5 text-sm">
            {[["Subtotal", doc.subtotal], ["Discount", -doc.discount], ["Taxable Amount", doc.taxable],
              ...(doc.intra_state ? [["CGST", doc.cgst], ["SGST", doc.sgst]] : [["IGST", doc.igst]]),
              ["Additional Charges", doc.additional_charges], ["Round Off", doc.round_off],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between text-slate-600 dark:text-slate-300">
                <span>{k}</span><span className="font-mono">{inr(v)}</span>
              </div>
            ))}
            <div className="flex justify-between border-t border-slate-200 dark:border-slate-700 pt-1.5 font-display font-bold">
              <span>Grand Total</span><span className="font-mono" data-testid="doc-total">{inr(doc.total)}</span>
            </div>
            {kind === "proforma" && (
              <div className="flex justify-between text-emerald-600 dark:text-emerald-400">
                <span>Advance Received</span><span className="font-mono" data-testid="proforma-advance-total">{inr(doc.advance_received || 0)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <PaymentDialog open={advanceOpen} presetParty={doc.party_id} proformaId={id}
        onClose={() => setAdvanceOpen(false)} onSaved={load} />
    </div>
  );
}
